import { ReflectUtils } from "@dooboostore/core";
import { getElementConfig, ensureInit } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
const QUERY_METADATA_KEY = Symbol.for("simple-web-component:query");
const resolveQueryElements = (inst, selector, options, win) => {
  const hostSet = SwcUtils.getHelperAndHostSet(inst);
  let all = [];
  if (typeof selector === "function") {
    const result = selector(inst, hostSet);
    if (result instanceof win.NodeList)
      all = Array.from(result).filter((e) => e instanceof win.HTMLElement);
    else if (Array.isArray(result))
      all = result.filter((e) => e instanceof win.HTMLElement);
    else if (result instanceof win.HTMLElement)
      all = [result];
  } else if (typeof selector === "string") {
    const r = options?.root || "auto";
    if (selector === "$this")
      all = [inst];
    else if (selector === "$host")
      all = hostSet.$host ? [hostSet.$host] : [];
    else if (selector === "$parentHost")
      all = hostSet.$parentHost ? [hostSet.$parentHost] : [];
    else if (selector === "$appHost")
      all = hostSet.$appHost ? [hostSet.$appHost] : [];
    else if (selector === "$firstHost")
      all = hostSet.$firstHost ? [hostSet.$firstHost] : [];
    else if (selector === "$lastHost")
      all = hostSet.$lastHost ? [hostSet.$lastHost] : [];
    else if (selector === "$firstAppHost")
      all = hostSet.$firstAppHost ? [hostSet.$firstAppHost] : [];
    else if (selector === "$lastAppHost")
      all = hostSet.$lastAppHost ? [hostSet.$lastAppHost] : [];
    else if (selector === "$hosts")
      all = hostSet.$hosts.filter(Boolean);
    else if (selector === "$appHosts")
      all = hostSet.$appHosts.filter(Boolean);
    else {
      const scopes = [];
      if (r === "auto")
        scopes.push(inst.shadowRoot || inst);
      else if (r === "light")
        scopes.push(inst);
      else if (r === "shadow" && inst.shadowRoot)
        scopes.push(inst.shadowRoot);
      else if (r === "all") {
        scopes.push(inst);
        if (inst.shadowRoot)
          scopes.push(inst.shadowRoot);
      }
      for (const scope of scopes) {
        const found = scope?.querySelectorAll?.(selector);
        if (found?.length)
          all.push(...Array.from(found).filter((e) => e instanceof win.HTMLElement));
      }
    }
  }
  if (options?.filter) {
    all = all.filter((el) => options.filter(el, { currentThis: inst, helper: hostSet }));
  }
  return all;
};
const pickElements = (all, pick) => {
  if (pick === "all")
    return all;
  if (pick === "even")
    return all.filter((_, i) => i % 2 === 0);
  if (pick === "odd")
    return all.filter((_, i) => i % 2 === 1);
  if (typeof pick === "number")
    return all[pick] ?? null;
  if (pick === "last")
    return all.length ? all[all.length - 1] : null;
  return all.length ? all[0] : null;
};
function query(selectorOrTarget, optionsOrPropertyKey, descriptor) {
  if (descriptor !== void 0 && (typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol")) {
    throw new Error(`@query decorator cannot be used on methods. (Method: ${String(optionsOrPropertyKey)})`);
  }
  if (selectorOrTarget && (typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol")) {
    return query("$this")(selectorOrTarget, optionsOrPropertyKey);
  }
  if (typeof selectorOrTarget === "string" || typeof selectorOrTarget === "function") {
    return (targetObj, propertyKey, descriptor2) => {
      if (descriptor2 !== void 0) {
        throw new Error(`@query decorator cannot be used on methods. (Method: ${String(propertyKey)})`);
      }
      const constructor = targetObj.constructor;
      let queries = ReflectUtils.getOwnMetadata(QUERY_METADATA_KEY, constructor);
      if (!queries) {
        queries = [];
        ReflectUtils.defineMetadata(QUERY_METADATA_KEY, queries, constructor);
      }
      queries.push({ propertyKey, selector: selectorOrTarget, options: optionsOrPropertyKey || {} });
      Object.defineProperty(targetObj, propertyKey, {
        get() {
          ensureInit(this);
          const config = getElementConfig(this);
          const win = config?.window || window;
          const options = optionsOrPropertyKey || {};
          if (typeof selectorOrTarget === "string" && selectorOrTarget === "$window")
            return win;
          if (typeof selectorOrTarget === "string" && selectorOrTarget === "$document")
            return win.document;
          const all = resolveQueryElements(this, selectorOrTarget, options, win);
          return pickElements(all, options.pick ?? "first");
        },
        set(nv) {
          ensureInit(this);
          if (nv === null || nv === void 0 || Array.isArray(nv) && nv.length === 0) {
            const config = getElementConfig(this);
            const win = config?.window || window;
            const options = optionsOrPropertyKey || {};
            const all = resolveQueryElements(this, selectorOrTarget, options, win);
            const picked = pickElements(all, options.pick ?? "first");
            if (Array.isArray(picked))
              picked.forEach((t) => t.remove());
            else if (picked)
              picked.remove();
          }
        },
        enumerable: true,
        configurable: true
      });
    };
  }
  return query("$this", selectorOrTarget);
}
const getQueryMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(QUERY_METADATA_KEY, constructor).flat();
};
const QUERY_ALL_METADATA_KEY = QUERY_METADATA_KEY;
function queryAll(selectorOrTarget, optionsOrPropertyKey, descriptor) {
  if (descriptor !== void 0 && (typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol")) {
    throw new Error(`@queryAll decorator cannot be used on methods. (Method: ${String(optionsOrPropertyKey)})`);
  }
  if (selectorOrTarget && (typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol")) {
    return query("$this", { pick: "all" })(selectorOrTarget, optionsOrPropertyKey);
  }
  if (typeof selectorOrTarget === "string" || typeof selectorOrTarget === "function") {
    return query(selectorOrTarget, { ...optionsOrPropertyKey ?? {}, pick: "all" });
  }
  return query({ ...selectorOrTarget ?? {}, pick: "all" });
}
const getQueryAllMetadata = getQueryMetadata;
function queryShadow(selector, options) {
  return query(selector, { ...options ?? {}, root: "shadow" });
}
function queryLight(selector, options) {
  return query(selector, { ...options ?? {}, root: "light" });
}
function queryAllRoots(selector, options) {
  return query(selector, { ...options ?? {}, root: "all" });
}
function queryAllShadow(selector, options) {
  return query(selector, { ...options ?? {}, root: "shadow", pick: "all" });
}
function queryAllLight(selector, options) {
  return query(selector, { ...options ?? {}, root: "light", pick: "all" });
}
function queryAllAll(selector, options) {
  return query(selector, { ...options ?? {}, root: "all", pick: "all" });
}
const queryIn = (root, pick) => (selector, options) => query(selector, { ...options ?? {}, root, ...pick !== void 0 ? { pick } : {} });
export {
  QUERY_ALL_METADATA_KEY,
  QUERY_METADATA_KEY,
  getQueryAllMetadata,
  getQueryMetadata,
  pickElements,
  query,
  queryAll,
  queryAllAll,
  queryAllLight,
  queryAllRoots,
  queryAllShadow,
  queryIn,
  queryLight,
  queryShadow,
  resolveQueryElements
};
//# sourceMappingURL=query.js.map
